import { TripDetail, TripStop, TripStopAddress } from '../types/trip.types';
import { TripStopCoordinate } from '../store/services/tripsApi';

export type TripMapRoutePoint = TripStopCoordinate & { stopId: number };

export function isValidLatLng(lat: number, lng: number): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return false;
  }
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    return false;
  }
  if (lat === 0 && lng === 0) {
    return false;
  }
  return true;
}

function getActivityDaySortKey(activities: TripStop[]): string {
  if (!activities?.length) {
    return '';
  }
  const a = activities[0];
  switch (a.stop_type) {
    case 'Flight':
      return a.departure_date_time;
    case 'Hotel':
      return a.check_in_date_time;
    case 'Ferry':
    case 'Cruise':
    case 'Rail':
    case 'Transportation':
      return a.departure_date_time;
    case 'BorderCrossing':
      return a.start_date_time;
    default:
      return a.start_date_time;
  }
}

function getStopSortTime(stop: TripStop): number {
  switch (stop.stop_type) {
    case 'Flight':
      return new Date(stop.departure_date_time).getTime();
    case 'Hotel':
      return new Date(stop.check_in_date_time).getTime();
    case 'Ferry':
    case 'Cruise':
    case 'Rail':
    case 'Transportation':
      return new Date(stop.departure_date_time).getTime();
    case 'BorderCrossing':
      return new Date(stop.start_date_time).getTime();
    default:
      return new Date(stop.start_date_time).getTime();
  }
}

export function getOrderedTripStops(trip: TripDetail): TripStop[] {
  const byDay = trip.trip_stops_by_day;
  if (!byDay || Object.keys(byDay).length === 0) {
    return [];
  }

  const sortedDays = Object.keys(byDay).sort((a, b) => {
    const dateA = new Date(getActivityDaySortKey(byDay[a])).getTime();
    const dateB = new Date(getActivityDaySortKey(byDay[b])).getTime();
    return dateA - dateB;
  });

  const out: TripStop[] = [];
  sortedDays.forEach(dayKey => {
    const acts = [...(byDay[dayKey] ?? [])];
    acts.sort((x, y) => getStopSortTime(x) - getStopSortTime(y));
    out.push(...acts);
  });
  return out;
}

function stopLabel(stop: TripStop): string {
  switch (stop.stop_type) {
    case 'Hotel':
    case 'Activity':
    case 'Concert':
    case 'Theatre':
    case 'Meeting':
    case 'Restaurant':
    case 'Tour':
    case 'Ferry':
    case 'Cruise':
    case 'Rail':
      return stop.name;
    case 'Transportation':
      return [stop.location_from_name, stop.location_to_name].filter(Boolean).join(' → ') || stop.vehicle_type;
    case 'Shopping':
      return stop.name || stop.place || stop.stop_type;
    case 'Parking':
    case 'Directions':
      return stop.place || stop.stop_type;
    case 'CarRental':
      return stop.name || stop.transport_name || stop.stop_type;
    case 'BorderCrossing':
      return stop.border_checkpoint || `${stop.country_from} → ${stop.country_to}`;
    case 'Flight':
      return `${stop.flight_from} → ${stop.flight_to}`;
  }
}

export function getStopPrimaryAddress(stop: TripStop): TripStopAddress | null {
  switch (stop.stop_type) {
    case 'Hotel':
    case 'Activity':
    case 'Concert':
    case 'Theatre':
    case 'Meeting':
    case 'Restaurant':
    case 'Tour':
    case 'Parking':
    case 'Directions':
    case 'BorderCrossing':
      return stop.address;
    case 'Shopping':
      return stop.address;
    case 'CarRental':
      return stop.pick_up_address ?? stop.address ?? null;
    case 'Ferry':
      return stop.departure_address ?? stop.arrival_address ?? null;
    case 'Cruise':
      return stop.starting_port_address ?? stop.ending_port_address ?? null;
    case 'Rail':
    case 'Transportation':
      return stop.departure_address ?? stop.arrival_address ?? null;
    case 'Flight':
    default:
      return null;
  }
}

export function tripStopToRoutePoint(stop: TripStop): TripMapRoutePoint | null {
  const addr = getStopPrimaryAddress(stop);
  if (!addr || !isValidLatLng(addr.latitude, addr.longitude)) {
    return null;
  }
  const name = (addr.name && addr.name.trim()) || stopLabel(stop);
  return {
    name,
    latitude: addr.latitude,
    longitude: addr.longitude,
    stopId: stop.id,
  };
}

export function assignPinRoles(points: TripStopCoordinate[]): TripStopCoordinate[] {
  const n = points.length;
  if (n === 0) {
    return points;
  }
  if (n === 1) {
    return [{ ...points[0], pinRole: 'only' }];
  }
  return points.map((p, i) => ({
    ...p,
    pinRole: i === 0 ? 'first' : i === n - 1 ? 'last' : 'middle',
  }));
}

export function spreadOverlappingCoordinates<T extends { latitude: number; longitude: number }>(points: T[]): T[] {
  const bucketCount = new Map<string, number>();
  return points.map(p => {
    const key = `${p.latitude.toFixed(4)}_${p.longitude.toFixed(4)}`;
    const n = bucketCount.get(key) ?? 0;
    bucketCount.set(key, n + 1);
    if (n === 0) {
      return { ...p };
    }
    const offset = 0.00013 * n;
    const angle = (2 * Math.PI * n) / 8;
    return {
      ...p,
      latitude: p.latitude + offset * Math.cos(angle),
      longitude: p.longitude + offset * Math.sin(angle),
    };
  });
}
